/**
 * Keeping a conversation pinned to its newest content.
 *
 * WHY THIS IS A MODULE AND NOT FOUR LINES IN A COMPONENT
 *   Scrolling cannot be observed in a headless or backgrounded browser - Chrome does not apply a scroll
 *   to a tab that is not being painted, so `scrollTop` reads back as 0 however it is set. That makes the
 *   behaviour untestable in place. Pulled out here it is ordinary logic over a tiny interface, and the
 *   rules that actually matter - when to follow, and when to leave the customer alone - are tested.
 *
 * THE RULE
 *   Follow the bottom while the customer is AT the bottom. The moment they scroll up they are reading
 *   something, and dragging them back down on the next repaint is worse than making them scroll down
 *   themselves. Being addressed again - a new message, a new step - re-pins the view.
 */

/** The part of a scrollable element this needs. An object literal satisfies it, so tests need no DOM. */
export interface Scrollable {
  scrollTop: number
  readonly scrollHeight: number
  readonly clientHeight: number
}

/**
 * How many pixels from the bottom still counts as "at the bottom".
 *
 * Not zero: fractional scroll positions, a device pixel ratio that is not 1, and a sub-pixel line height
 * all mean scrollTop rarely lands exactly on scrollHeight - clientHeight, and a stricter test reads
 * "already at the bottom" as "scrolled up" and then stops following.
 */
export const BOTTOM_TOLERANCE = 80

export interface StickyScroll {
  /** Whether the view is currently following the bottom. */
  readonly sticking: boolean
  /** Call on every scroll event: the customer's own scrolling is what decides whether to keep following. */
  onScroll(): void
  /** Call whenever the content changes size. Scrolls to the bottom only if still following. */
  follow(): void
  /** Force following again, for when the customer is addressed rather than merely shown more. */
  pin(): void
}

export function createStickyScroll(
  scroller: Scrollable,
  tolerance: number = BOTTOM_TOLERANCE,
): StickyScroll {
  let sticking = true

  const distanceFromBottom = () => scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight

  return {
    get sticking() {
      return sticking
    },
    onScroll() {
      sticking = distanceFromBottom() < tolerance
    },
    follow() {
      // Assigning past the maximum is harmless - the browser clamps it - and asking for scrollHeight is
      // more robust than computing the maximum here, where clientHeight may be mid-relayout.
      if (sticking) scroller.scrollTop = scroller.scrollHeight
    },
    pin() {
      sticking = true
    },
  }
}
